import { useEffect, type RefObject } from "react";

export const useChapterIntersection = ({
  containerRef,
  enabled,
  onRevealChapter,
  onActivateChapter,
}: {
  containerRef: RefObject<HTMLElement | null>;
  enabled: boolean;
  onRevealChapter: (id: string) => void;
  onActivateChapter: (id: string) => void;
}) => {
  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    const chapterElements = Array.from(
      container.querySelectorAll<HTMLElement>(".scroll-chapter"),
    );
    const ratios = new Map<string, number>();
    for (const chapterElement of chapterElements) {
      const id = chapterElement.dataset.chapterId;
      if (!id) continue;
      ratios.set(id, 0);
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.getAttribute("data-chapter-id");
          if (!id) continue;
          ratios.set(id, entry.isIntersecting ? entry.intersectionRatio : 0);
          if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
            onRevealChapter(id);
          }
        }

        let topId = "";
        let topRatio = -1;
        for (const [id, ratio] of ratios) {
          if (ratio > topRatio) {
            topRatio = ratio;
            topId = id;
          }
        }
        if (topRatio >= 0 && topId) {
          onActivateChapter(topId);
        }
      },
      {
        root: container,
        threshold: [0.2, 0.35, 0.5, 0.65, 0.8],
      },
    );

    for (const chapterElement of chapterElements) {
      observer.observe(chapterElement);
    }
    return () => observer.disconnect();
  }, [enabled, containerRef, onRevealChapter, onActivateChapter]);
};
